import { Camera, Monitor, MoveDiagonal, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { CalibrationChecklist } from '../components/CalibrationChecklist';
import { EndSessionDialog } from '../components/EndSessionDialog';
import { VideoPanel } from '../components/VideoPanel';
import { calibrationConfig } from '../config/calibration';
import { useExam } from '../context/ExamContext';
import { PhoneConnectionStatus } from '../context/PhonePairingContext';
import { usePhoneCameraSender } from '../hooks/usePhoneCameraSender';
import { useVideoCalibration } from '../hooks/useVideoCalibration';
import { useWebRtcPairing } from '../hooks/useWebRtcPairing';

export function PhonePairingPage() {
  const phoneRoomId = new URLSearchParams(window.location.search).get('room');
  return phoneRoomId ? <PhoneCameraSender roomId={phoneRoomId} /> : <DesktopPhonePairing />;
}

export function PhoneCameraSender({ roomId }: { roomId: string }) {
  const phone = usePhoneCameraSender(roomId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const calibration = useVideoCalibration(videoRef, phone.stream, {
    mode: 'mobile',
    required: true,
    connected: phone.status === 'connected'
  });
  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    if (phone.status === 'connected' && calibration.ready) setSetupComplete(true);
  }, [calibration.ready, phone.status]);

  if (phone.status === 'ended' || phone.done) {
    return (
      <main className="grid min-h-screen place-items-center bg-midnight px-4 text-slate-50">
        <section className="w-full max-w-md text-center" aria-live="polite">
          <ShieldCheck className="mx-auto h-14 w-14 text-mint" />
          <h1 className="mt-5 text-3xl font-black">Exam session completed</h1>
          <p className="mt-4 text-slate-200">Camera access has been stopped.</p>
          <p className="mt-2 text-slate-400">You may safely close this page.</p>
          {!phone.done ? <Button className="mt-7" onClick={phone.finish}>Done</Button> : null}
        </section>
      </main>
    );
  }

  if (setupComplete) {
    const selectedCamera = phone.devices.find((camera) => camera.deviceId === phone.selectedDeviceId);
    const reconnecting = phone.status === 'reconnecting' || phone.status === 'connecting' || phone.status === 'error';
    return (
      <main className="grid min-h-screen place-items-center bg-midnight px-4 py-8 text-slate-50">
        <section className="w-full max-w-xl">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase text-neon-blue">Secondary Camera</p>
              <h1 className="mt-1 text-2xl font-black">{reconnecting ? 'Reconnecting camera' : 'Camera connected'}</h1>
            </div>
            <span role="status" className={`rounded-md px-3 py-2 text-sm font-semibold ${reconnecting ? 'bg-amber-400/15 text-amber-200' : 'bg-emerald-400/15 text-emerald-300'}`}>
              {reconnecting ? 'Reconnecting' : 'Connected'}
            </span>
          </div>
          <div className="mt-5"><VideoPanel stream={phone.stream} videoRef={videoRef} label={selectedCamera?.label ?? 'Camera'} /></div>
          <p className="mt-5 text-center font-medium">Keep this phone in position until the exam is complete.</p>
          <p className="mt-2 text-center text-sm text-slate-400">Selected camera: {selectedCamera?.label ?? 'Rear Camera'}</p>
          {phone.devices.length > 1 ? (
            <div className="mt-5 flex gap-3">
              <label htmlFor="connected-phone-camera" className="sr-only">Camera</label>
              <select id="connected-phone-camera" className="focus-ring min-w-0 flex-1 rounded-md border border-white/15 bg-slate-900 p-3" value={phone.selectedDeviceId} onChange={(event) => phone.setSelectedDeviceId(event.target.value)}>
                {phone.devices.map((camera) => <option key={camera.deviceId} value={camera.deviceId}>{camera.label}</option>)}
              </select>
              <Button loading={phone.startingPreview} onClick={() => void phone.startPreview()}>Switch Camera</Button>
            </div>
          ) : null}
          {reconnecting ? <p className="mt-4 text-center text-sm text-amber-200">Secondary camera connection was interrupted. Reconnecting...</p> : null}
        </section>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="text-3xl font-bold">Set Up Secondary Camera</h1>
      <p className="mt-3 text-slate-600 dark:text-slate-300">Choose a phone camera, check the preview, then connect it to the exam.</p>

      {!window.isSecureContext ? (
        <div role="alert" className="mt-5 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Camera access requires HTTPS in iPhone Safari and Android Chrome. Reopen this pairing link using its secure HTTPS address.
        </div>
      ) : null}
      {phone.error ? <div role="alert" className="mt-5 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900">{phone.error}</div> : null}

      <section className="mt-6 rounded-md border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="font-semibold">1. Select Camera</h2>
        {phone.permissionStatus !== 'granted' ? (
          <Button className="mt-4" loading={phone.permissionStatus === 'requesting'} onClick={() => void phone.requestPermission()}>
            {phone.permissionStatus === 'requesting' ? 'Requesting Permission' : 'Grant Camera Permission'}
          </Button>
        ) : (
          <div className="mt-4">
            <label htmlFor="phone-camera" className="block text-sm font-semibold">Camera</label>
            <select
              id="phone-camera"
              className="focus-ring mt-2 w-full rounded-md border border-slate-300 bg-white p-3 text-slate-900"
              value={phone.selectedDeviceId}
              onChange={(event) => phone.setSelectedDeviceId(event.target.value)}
            >
              {phone.devices.map((camera) => <option key={camera.deviceId} value={camera.deviceId}>{camera.label}</option>)}
            </select>
            <Button className="mt-4" loading={phone.startingPreview} onClick={() => void phone.startPreview()}>
              {phone.stream ? 'Change Camera' : 'Start Preview'}
            </Button>
          </div>
        )}
      </section>

      <section className="mt-5">
        <h2 className="font-semibold">2. Preview Camera</h2>
        <div className="mt-3"><VideoPanel stream={phone.stream} videoRef={videoRef} label={phone.startingPreview ? 'Starting camera' : phone.stream ? 'Camera active' : 'Preview not started'} /></div>
        {phone.stream ? <Button className="mt-3" variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void phone.startPreview()}>Restart Preview</Button> : null}
      </section>

      <PhonePlacementGuide />

      <section className="mt-6 rounded-md border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <CalibrationChecklist items={calibration.items} title="4. Complete Camera Check" />
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="secondary" onClick={calibration.retry}>Retry Checks</Button>
          {phone.status !== 'connected' ? (
            <Button disabled={!calibration.preConnectionReady || phone.status === 'connecting'} loading={phone.status === 'connecting'} onClick={() => void phone.connect()}>
              {phone.status === 'connecting' ? 'Connecting Camera' : 'Connect Camera'}
            </Button>
          ) : <p className="flex items-center gap-2 font-semibold text-emerald-400"><ShieldCheck className="h-5 w-5" />Secondary camera connected</p>}
        </div>
      </section>
    </main>
  );
}

export function PhonePlacementGuide() {
  return (
    <section className="mt-6 rounded-md border border-neon-blue/25 bg-neon-blue/10 p-5">
      <h2 className="font-semibold">3. Position Phone</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-[150px_1fr] sm:items-center">
        <div aria-hidden="true" className="relative mx-auto h-28 w-36 rounded-md border border-white/15 bg-midnight/70">
          <Monitor className="absolute bottom-3 left-4 h-10 w-10 text-neon-blue" />
          <Camera className="absolute right-4 top-3 h-9 w-9 rotate-[-18deg] text-mint" />
          <MoveDiagonal className="absolute left-[62px] top-10 h-7 w-7 text-slate-300" />
        </div>
        <div>
          <p className="font-medium">Place the phone at a side angle that clearly shows you and your workspace.</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">
            <li>Keep the desk, keyboard, and your hands visible where possible.</li>
            <li>Use a stable surface or stand at roughly a 45-degree side angle where practical.</li>
            <li>Keep the lens clean, clear, and unobstructed.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function desktopStatus(status: PhoneConnectionStatus, hasConnected: boolean, mobileRequired: boolean) {
  if (status === 'ended' || status === 'closed') return { title: 'Secondary camera session ended', detail: 'The phone camera is no longer connected to this session.', tone: 'text-slate-300' };
  if (status === 'connected') return { title: 'Secondary camera connected', detail: 'Keep the phone in position until the exam is complete.', tone: 'text-emerald-300' };
  if (status === 'connecting' || status === 'reconnecting') return { title: 'Reconnecting secondary camera...', detail: 'Keep both devices online while the connection recovers.', tone: 'text-amber-200' };
  if (status === 'disconnected' || status === 'failed') return { title: 'Secondary camera connection interrupted', detail: hasConnected ? 'Check the phone and reconnect to continue.' : 'Check the phone and scan the pairing code again.', tone: 'text-amber-200' };
  return mobileRequired
    ? { title: 'Waiting for secondary camera', detail: 'Scan the pairing code with the phone to continue.', tone: 'text-slate-200' }
    : { title: 'Secondary camera is optional for this public demo.', detail: 'Scan the pairing code to add a second camera, or continue without one.', tone: 'text-slate-200' };
}

export function DesktopPhonePairing() {
  const { pairingUrl, remoteStream, status, reconnect, endSession } = useWebRtcPairing();
  const { state, logout } = useExam();
  const navigate = useNavigate();
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [reconnectRequested, setReconnectRequested] = useState(false);
  const mobileRequired = calibrationConfig.publicDemo.mobileCameraRequired;
  const hasConnected = Boolean(remoteStream);
  const terminal = status === 'ended' || status === 'closed' || Boolean(state.finishedAt);
  const examRunning = Boolean(state.startedAt) && !state.finishedAt;
  const setupAllowed = state.candidate?.role === 'candidate'
    && state.workflow.cameraSetupCompleted
    && state.calibration?.readiness === 'ready'
    && !examRunning
    && !terminal;
  const progress = status === 'connecting' || status === 'reconnecting';
  const interrupted = status === 'disconnected' || status === 'failed';
  const showReconnect = setupAllowed && hasConnected && interrupted && !reconnectRequested;
  const showSetupActions = setupAllowed && !progress;
  const statusCopy = desktopStatus(terminal ? 'ended' : status, hasConnected, mobileRequired);
  const showQr = !hasConnected && !terminal;

  const confirmEndSession = useCallback(() => {
    endSession();
    logout();
    navigate('/');
  }, [endSession, logout, navigate]);

  useEffect(() => {
    if (!interrupted) setReconnectRequested(false);
  }, [interrupted]);

  const requestReconnect = () => {
    if (reconnectRequested) return;
    setReconnectRequested(true);
    reconnect();
  };

  if (terminal) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 text-center" aria-live="polite">
        <ShieldCheck className="mx-auto h-12 w-12 text-slate-400" />
        <h1 className="mt-5 text-3xl font-bold">{statusCopy.title}</h1>
        <p className="mt-3 text-slate-400">{statusCopy.detail}</p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-12">
      <h1 className="text-3xl font-bold">Secondary Camera Setup</h1>
      <div className={`mt-8 grid gap-6 ${hasConnected ? 'lg:grid-cols-[320px_minmax(0,1fr)]' : 'lg:grid-cols-[360px_minmax(0,1fr)]'}`}>
        <div className="rounded-md border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          {showQr ? <QRCodeCanvas value={pairingUrl} size={260} includeMargin aria-label="Scan to set up the secondary camera" /> : <ShieldCheck className="h-10 w-10 text-emerald-400" />}
          <p role="status" className={`mt-5 text-lg font-bold ${statusCopy.tone}`}>{statusCopy.title}</p>
          <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">{statusCopy.detail}</p>
        </div>
        {hasConnected ? (
          <VideoPanel stream={remoteStream} label="Secondary camera preview" muted={false} />
        ) : (
          <div className="flex min-h-64 items-center rounded-md border border-neon-blue/20 bg-neon-blue/10 p-6">
            <div>
              <h2 className="text-lg font-bold">Position the phone at a side angle</h2>
              <p className="mt-3 leading-7 text-slate-300">Keep the candidate, desk, keyboard, and surrounding workspace visible. Use a stable stand and leave the lens unobstructed.</p>
            </div>
          </div>
        )}
      </div>

      {showSetupActions ? (
        <div className="mt-7 flex flex-wrap items-center gap-3">
          {showReconnect ? <Button variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={requestReconnect}>Reconnect</Button> : null}
          <Link to="/camera-setup"><Button variant="secondary">Redo Laptop Setup</Button></Link>
          <Button variant="danger" onClick={() => setConfirmingEnd(true)}>End Session</Button>
          <Link to="/exam" className="sm:ml-auto">
            <Button disabled={mobileRequired && status !== 'connected'}>Continue to Mock Exam</Button>
          </Link>
        </div>
      ) : null}

      <EndSessionDialog open={confirmingEnd} onClose={() => setConfirmingEnd(false)} onConfirm={confirmEndSession} />
    </section>
  );
}
